import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import userWorkflowToContext from '@helpers/userWorkflowToContext';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import Spacer from '@components/Spacer';
import Check from '@components/Icons/Check';
import ProgressIndicator from '@components/ProgressIndicator';
import { getUserWorkflowById, getUserWorkflowCollaborators } from '@tsClient/workflows';
import MainPanelSubLayout from '@layouts/MainPanelSubLayout';
import { DocumentAccessContext, DocumentAccessGate } from '@providers/DocumentAccess';
import DocumentPreview from '@components/Document/DocumentPreview';
import { useQueryState } from 'nuqs';
import { DocumentRequestFileStatus } from '@/tsClient/servers/noodleDocuments/document-api-models';
import TeamsContext from '@providers/Teams/TeamsContext';
import RejectDocumentModal from '@modals/RejectDocumentModal';
import { Reorder } from 'framer-motion';
import File from '@components/DesignLibrary/File';
import Buttons from '@components/Buttons';
import DotsSixVertical from '@components/Icons/DotsSixVertical';
import Button from '@components/DesignLibrary/Button';
import DocumentUploader, { DocumentUploaderRef } from '@components/Document/DocumentUploader';
import Bluebird from 'bluebird';
import BannerStatus from '@components/DesignLibrary/BannerStatus';
import { usePrevious, useUser } from '@hooks';
import DocumentStatus from '@components/Document/DocumentStatus';
import Header from '@/components/DesignLibrary/Header';
import { useEventsContext } from '@/providers/Events';
import { WorkflowCollaborator } from '@/hooks/useUserWorkflowContext';
import createRichTextAst from '@/helpers/createRichTextAst';
import DocumentSort from '@/components/Document/DocumentSort';
import * as format from '@format';

import UserWorkflowDiscussion from '../Creators/Dashboard/Workflows/UserWorkflowWidgets/UserWorkflowDiscussion';

import s from './DocumentRequestUserFileDetail.module.scss';

type Props = {
  documentRequestUserFileId: string;
};

export const DOC_ID_QS_KEY = 'documentId';

type Tabs = 'Files' | 'Discussion';

const DocumentRequestUserFileDetail: FC<Props> = ({ documentRequestUserFileId }) => {
  const [user] = useUser();
  const userId = user?.id;
  const [documentId, setDocumentId] = useQueryState(DOC_ID_QS_KEY);

  const [isRejectModalOpen, setRejectModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [removingFileId, setRemovingFileId] = useState<string | null>(null);
  const [isRemovingAllFiles, setIsRemovingAllFiles] = useState(false);
  const [files, setFiles] = useState<Array<Parameters<typeof File>[0]['document']>>([]);
  const [isCollaborator, setIsCollaborator] = useState(false);
  const [collaborators, setCollaborators] = useState<WorkflowCollaborator[]>();
  const [docRequestUserFile, setDocRequestUserFile] = useState<Awaited<
    ReturnType<typeof tsClient.documentRequests.getDocumentRequestUserFile>
  > | null>(null);
  const [rightPanelTab, setRightPanelTab] = useState<Tabs>('Files');
  const previousFiles = usePrevious(files);
  const { accessToken } = useContext(DocumentAccessContext);
  const { creatorId } = useContext(TeamsContext);
  const { emit } = useEventsContext();

  const {
    data: workflowData,
    fetchingState: fetchingWorkflow,
    getData: getWorkflow,
  } = useNoodleApi(getUserWorkflowById, {
    toastOnError: true,
  });
  const { getData: getMessages } = useNoodleApi(tsClient.workflowConversations.getWorkflowConversationMessages);
  const { getData: sendMessage } = useNoodleApi(tsClient.workflowConversations.createWorkflowConversationMessage);

  const { data: loadedCollaborators, getData: getCollaboratorsFn } = useNoodleApi(getUserWorkflowCollaborators);
  const { getData: getDocumentRequestUserFile } = useNoodleApi(tsClient.documentRequests.getDocumentRequestUserFile);
  const [parentId, setParentId] = useState('');
  const { getData: removeDocument } = useNoodleApi(tsClient.documentRequests.removeDocumentFromDocumentRequestUserFile);
  const { getData: updateFile } = useNoodleApi(tsClient.documentRequests.updateDocumentRequestUserFile);
  const { getData: updateDocumentOrder } = useNoodleApi(tsClient.documentRequests.updateDocumentOrder);
  const { getData: getDocumentRequestUserPermissions } = useNoodleApi(tsClient.documentRequests.getDocumentRequestUserPermissions);
  const [comment, setComment] = useState<Awaited<ReturnType<typeof tsClient.workflowConversations.createWorkflowConversationMessage>>>();

  const reorderContainerRef = useRef<HTMLUListElement>(null);
  const documentUploaderRef = useRef<DocumentUploaderRef>(null);

  const fetchCollaborators = useCallback(() => {
    if (workflowData?.id) {
      getCollaboratorsFn({ userWorkflowId: workflowData?.id });
    }
  },[workflowData,getCollaboratorsFn]);

  useEffect(() => {
    fetchCollaborators();
  }, [fetchCollaborators]);

  useEffect(() => {
    if (loadedCollaborators) {
      setCollaborators(loadedCollaborators);
    }
  }, [loadedCollaborators, setCollaborators]);

  useEffect(() => {
    if (docRequestUserFile?.documentRequestUser?.userWorkflowId) {
      getWorkflow({ userWorkflowId: docRequestUserFile.documentRequestUser.userWorkflowId });
    }
  }, [docRequestUserFile?.documentRequestUser?.userWorkflowId, getWorkflow]);

  const documentRequestUserId = docRequestUserFile?.documentRequestUser?.id;
  useEffect(() => {
    const checkIsCollaborator = async (): Promise<void> => {
      if (userId && documentRequestUserId) {
        const response = await getDocumentRequestUserPermissions({ id: documentRequestUserId });
        if (response.data) {
          const myPermissions = response.data.find(p => p.personId === userId);
          setIsCollaborator(!!myPermissions);
        }
      }
    };
    checkIsCollaborator();
  }, [userId, documentRequestUserId, getDocumentRequestUserPermissions]);

  useEffect(() => {
    if (docRequestUserFile?.documents) {
      setFiles(docRequestUserFile.documents);
    }
  }, [docRequestUserFile?.documents]);

  const autoMessageOnDocumentReject = async (reason: string):Promise<void> => {
    const { data: commentFetched } = await sendMessage({
      contextId: documentRequestUserFileId,
      contextType: "document-request-file",
      parentId: parentId ?? undefined,
      text: createRichTextAst(`Document rejected.\nReason: ${reason}`),
      userWorkflowId: workflowData?.id ?? '',
    });
    if(commentFetched?.id) {
      setComment(commentFetched);
    }
  };
  const fetchDocument = useCallback(async (): Promise<void> => {
    setIsUploading(false);
    const res = await getDocumentRequestUserFile({
      fileId: documentRequestUserFileId,
    });
    if (res.data) {
      setDocRequestUserFile(res.data);
    }
  }, [documentRequestUserFileId, getDocumentRequestUserFile]);

  const fetchMessage = useCallback(async (): Promise<void> => {
    const { data } = await getMessages({
      contextId: documentRequestUserFileId,
      page: 1,
      perPage: 5,
      userWorkflowId: workflowData?.id ?? '',
    });
    const parentIdLocal = data?.items?.find(d => d?.contextId===documentRequestUserFileId)?.id;
    setParentId(parentIdLocal ?? '');
  },[getMessages, documentRequestUserFileId, workflowData]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  useEffect(() => {
    fetchMessage();
  }, [fetchMessage]);

  useEffect(() => {
    if (docRequestUserFile?.documents?.[0] && !documentId) {
      setDocumentId(docRequestUserFile.documents[0].id);
    }
  }, [setDocumentId, docRequestUserFile, documentId]);

  useEffect(() => {
    if (docRequestUserFile && files.length > 0 && (previousFiles?.length || 0) > 0) {
      updateDocumentOrder({
        fileId: documentRequestUserFileId,
        id: docRequestUserFile.documentRequestUser.id,
        updates: files.map((f, idx) => ({
          documentId: f.id,
          rank: idx + 1,
        })),
      });
      emit('docRequestUpdate', { id: docRequestUserFile.documentRequestUser.id });
    }
  }, [files, previousFiles, docRequestUserFile, updateDocumentOrder, documentRequestUserFileId, emit]);

  if (!docRequestUserFile) {
    return (
      <>
        <Spacer size={80} />
        <ProgressIndicator isCentered isPage />
      </>
    );
  }

  const uploadFile = (): void => {
    documentUploaderRef.current?.uploadDocument();
  };

  const removeFile = async (documentIdToRemove: string): Promise<void> => {
    if (window.confirm('Are you sure you want to remove this file?')) {
      setRemovingFileId(documentIdToRemove);
      await removeDocument({
        documentId: documentIdToRemove,
        fileId: docRequestUserFile.id,
        id: docRequestUserFile.documentRequestUser.id,
      });
      await fetchDocument();
      emit('docRequestUpdate', { id: docRequestUserFile.documentRequestUser.id });
      setRemovingFileId(null);
    }
  };

  const removeAllFiles = async (): Promise<void> => {
    if (window.confirm('Are you sure you want to remove all files?')) {
      setIsRemovingAllFiles(true);
      // todo - single endpoint to remove all documents
      await Bluebird.map(docRequestUserFile.documents, async document => {
        await removeDocument({
          documentId: document.id,
          fileId: docRequestUserFile.id,
          id: docRequestUserFile.documentRequestUser.id,
        });
      });
      await fetchDocument();
      emit('docRequestUpdate', { id: docRequestUserFile.documentRequestUser.id });
      setIsRemovingAllFiles(false);
    }
  };

  const acceptFiles = async (): Promise<void> => {
    await updateFile({
      fileId: documentRequestUserFileId,
      id: docRequestUserFile.documentRequestUser.id,
      status: DocumentRequestFileStatus.COMPLETE,
    });
    emit('docRequestUpdate', { id: docRequestUserFile.documentRequestUser.id });
    await fetchDocument();
  };

  const document = docRequestUserFile.documents.find(d => d.id === documentId);
  const canReject = creatorId === docRequestUserFile.creatorId && docRequestUserFile.status !== DocumentRequestFileStatus.ACTION_REQUIRED;
  return (
    <>
      <Head>
        <title>{docRequestUserFile.fileName || docRequestUserFile.documentRequestFile?.fileName}</title>
      </Head>

      <MainPanelSubLayout
        header={{
          actions: [
            ...((docRequestUserFile.status === DocumentRequestFileStatus.ACTION_REQUIRED
              && creatorId === docRequestUserFile.creatorId && [
              {
                icon: Check,
                label: 'Accept document',
                onClick: acceptFiles,
              },
            ])
              || []),
            ...((canReject && [
              {
                isDestructive: true,
                label: 'Reject document',
                onClick: () => setRejectModalOpen(true),
              },
            ])
              || []),
          ],
          title: docRequestUserFile.fileName || docRequestUserFile.documentRequestFile?.fileName || 'Requested Document',
        }}
        pageContext={{
          isFetching: !workflowData,
          items: workflowData ? userWorkflowToContext(workflowData) : [],
        }}
        rightPanel={{
          component: (
            <div className={s.rightPanel}>
              <Header
                tabs={[
                  {
                    label: 'Files',
                    onClick: () => setRightPanelTab('Files'),
                    selected: rightPanelTab === 'Files',
                  },
                  {
                    label: 'Discussion',
                    onClick: () => setRightPanelTab('Discussion'),
                    selected: rightPanelTab === 'Discussion',
                  },
                ]}
                isSticky
                className={s.header}
              />

              {rightPanelTab === 'Files' && (
                <>
                  <DocumentUploader
                    isMultiple
                    ref={documentUploaderRef}
                    documentRequestUserFileId={documentRequestUserFileId}
                    onUploadProgressChange={() => {}}
                    onUploadError={() => {}}
                    onFinishUpload={async () => {
                      await fetchDocument();
                      emit('docRequestUpdate', { id: docRequestUserFile.documentRequestUser.id });
                      setIsUploading(false);
                    }}
                    onUploadStart={() => setIsUploading(true)}
                  />
                  {docRequestUserFile.rejectionMessage && docRequestUserFile.status === DocumentRequestFileStatus.ACTION_REQUIRED && (
                    <div className={s.rejectionContainer}>
                      <BannerStatus status="error" label={`${docRequestUserFile.rejectionMessage}${docRequestUserFile.rejectedAt
                        ? ` (${format.datetimeLocalTZ.withoutTimezone({ datetime: docRequestUserFile.rejectedAt })})`
                        : ''}`} />
                    </div>
                  )}
                  <DocumentSort files={files} setFiles={setFiles} className={s.sort} />
                  <Reorder.Group ref={reorderContainerRef} values={files} onReorder={setFiles} axis="y" className={s.table}>
                    {files.map(rightPanelDocument => (
                      <Reorder.Item
                        whileDrag={{ backgroundColor: '#ffffff', cursor: 'grabbing' }}
                        value={rightPanelDocument}
                        key={rightPanelDocument.id}
                        dragConstraints={reorderContainerRef}
                        style={documentId === rightPanelDocument.id ? { backgroundColor: '#f7f7f7' } : {}}
                      >
                        <Buttons
                          isWrapper
                          onClick={() => setDocumentId(rightPanelDocument.id)}
                          ariaLabel={rightPanelDocument.fileName}
                          style={{ paddingBottom: 32 }}
                        >
                          <div className={s.document}>
                            <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'row', width: '100%' }}>
                              <File
                                key={rightPanelDocument.fileName}
                                document={rightPanelDocument}
                                allowRename
                                onRename={async () => {
                                  await fetchDocument();
                                }}
                              />
                              <DotsSixVertical size={24} color="var(--color-gray-50)" />
                            </div>
                          </div>
                        </Buttons>
                        <div className={s.documentFooter}>
                          <DocumentStatus documentRequestUserFileId={documentRequestUserFileId} document={rightPanelDocument} />
                          <Button
                            variant="destructive"
                            size="xs"
                            onClick={() => removeFile(rightPanelDocument.id)}
                            loading={removingFileId === rightPanelDocument.id}
                            className={s.removeButton}
                          >
                            Remove
                          </Button>
                        </div>
                      </Reorder.Item>
                    ))}
                  </Reorder.Group>
                  <div className={s.buttons}>
                    <Button loading={isUploading} fullWidth variant="primary" size="sm" onClick={uploadFile}>
                      Upload more files
                    </Button>
                    <Spacer size={8} />
                    <Button fullWidth variant="destructive" size="sm" onClick={removeAllFiles} loading={isRemovingAllFiles}>
                      Remove all
                    </Button>
                  </div>
                </>
              )}

              {rightPanelTab === 'Discussion' && workflowData && (
                <UserWorkflowDiscussion
                  loadedCollaborators = {collaborators}
                  rejectionComment= {comment}
                  isLoaded={fetchingWorkflow?.isFetching === false}
                  userWorkflow={workflowData}
                  contextId={documentRequestUserFileId}
                  contextType="document-request-file"
                  isAsync
                />
              )}
            </div>
          ),
          title: 'Files',
        }}
        homeTabTitle="Document"
      >
        <div className={s.container}>
          {docRequestUserFile.documents.length === 0 && (
            <div className={s.empty}>
              <p className="body-md">No documents have been uploaded yet.</p>
            </div>
          )}
          {document && (
            <DocumentAccessGate
              documentCreatorId={docRequestUserFile.creatorId}
              documentPersonId={docRequestUserFile.personId}
              isCollaborator={isCollaborator}
              isCenteredVertically
            >
              <div style={{ width: '100%' }}>
                <DocumentPreview
                  document={document}
                  accessToken={accessToken}
                  canDownload={true}
                  canEvaluateWithAi={true}
                  documentRequestFileDescription={docRequestUserFile.fileDescription}
                  documentRequestFileName={docRequestUserFile.fileName}
                  internalDataType={docRequestUserFile.internalDataType}
                  onRename={async () => {
                    await fetchDocument();
                    emit('docRequestUpdate', { id: docRequestUserFile.documentRequestUser.id });
                  }}
                />
              </div>
            </DocumentAccessGate>
          )}
        </div>
      </MainPanelSubLayout>
      {isRejectModalOpen && (
        <RejectDocumentModal
          triggerOnReject = {autoMessageOnDocumentReject}
          refetchDocument={fetchDocument}
          documentRequestUserId={docRequestUserFile.documentRequestUser.id}
          fileId={docRequestUserFile.id}
          onClose={() => setRejectModalOpen(false)}
        />
      )}
    </>
  );
};

export default DocumentRequestUserFileDetail;
